import { BrowserRouter, Route, Routes } from 'react-router-dom'
import "./index.css";
import { AuthProvider, RequireAuth } from './lib/auth'
import { FarmsProvider } from './lib/farms'
import { AskPage } from './pages/ask-page'
import { AssetFormPage } from './pages/asset-form-page'
import { AssetPage } from './pages/asset-page'
import { DashboardPage } from './pages/dashboard-page'
import { FarmPage } from './pages/farm-page'
import { FieldPage } from './pages/field-page'
import { InventoryPage } from './pages/inventory-page'
import { SettingsPage } from './pages/settings-page'
import { StockEntryPage } from './pages/stock-entry-page'
import { StockItemFormPage } from './pages/stock-item-form-page'
import { StockItemPage } from './pages/stock-item-page'
import { LoginPage } from './pages/login-page'
import { PesticidePage } from './pages/pesticide-page'
import { PesticidesPage } from './pages/pesticides-page'
import { FertilizerPage } from './pages/fertilizer-page'
import { FertilizersPage } from './pages/fertilizers-page'
import { AmendmentLogsPage } from './farmlogs/amendment-logs-page'
import { FertilizerLogsPage } from './farmlogs/fertilizer-logs-page'
import { HarvestLogsPage } from './farmlogs/harvest-logs-page'
import { FieldTasksPage, ScoutingObservationsPage } from './farmlogs/field-logs-page'
import { SprayLogsPage } from './farmlogs/spray-logs-page'
import { BuildingInspectionsPage } from './farmlogs/building-inspections-page'
import { EquipmentActivitiesPage } from './farmlogs/equipment-activities-page'
import { HygieneFacilityChecksPage } from './farmlogs/hygiene-facility-checks-page'
import { PestMonitoringPage } from './farmlogs/pest-monitoring-page'
import { TrainingSessionsPage } from './farmlogs/training-sessions-page'
import { PackingRecordsPage } from './farmlogs/packing-records-page'
import { ShipmentsPage } from './farmlogs/shipments-page'
import { VisitorEntriesPage } from './farmlogs/visitor-entries-page'
import { LogStartPage } from './wizard/log-start-page'
import { LogFlowPage } from './wizard/wizard-shell'

/** Farm-level CanadaGAP records (Forms G, I, J, K, L, M, O, Q): path under /farms/:farmId and page. */
const FARM_RECORD_ROUTES = [
  { path: 'building-inspections', page: <BuildingInspectionsPage /> },
  { path: 'equipment-activities', page: <EquipmentActivitiesPage /> },
  { path: 'hygiene-facility-checks', page: <HygieneFacilityChecksPage /> },
  { path: 'training-sessions', page: <TrainingSessionsPage /> },
  { path: 'visitor-entries', page: <VisitorEntriesPage /> },
  { path: 'pest-monitoring-checks', page: <PestMonitoringPage /> },
  { path: 'packing-records', page: <PackingRecordsPage /> },
  { path: 'shipments', page: <ShipmentsPage /> },
]

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <FarmsProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/"
              element={
                <RequireAuth>
                  <DashboardPage />
                </RequireAuth>
              }
            />
            <Route
              path="/farms/:farmId"
              element={
                <RequireAuth>
                  <FarmPage />
                </RequireAuth>
              }
            />
            <Route
              path="/settings"
              element={
                <RequireAuth>
                  <SettingsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/farms/:farmId/inventory"
              element={
                <RequireAuth>
                  <InventoryPage />
                </RequireAuth>
              }
            />
            <Route
              path="/farms/:farmId/inventory/new"
              element={
                <RequireAuth>
                  <AssetFormPage />
                </RequireAuth>
              }
            />
            <Route
              path="/farms/:farmId/inventory/stock/new"
              element={
                <RequireAuth>
                  <StockItemFormPage />
                </RequireAuth>
              }
            />
            <Route
              path="/farms/:farmId/inventory/stock/:stockItemId"
              element={
                <RequireAuth>
                  <StockItemPage />
                </RequireAuth>
              }
            />
            <Route
              path="/farms/:farmId/inventory/stock/:stockItemId/edit"
              element={
                <RequireAuth>
                  <StockItemFormPage />
                </RequireAuth>
              }
            />
            <Route
              path="/farms/:farmId/inventory/stock/:stockItemId/record"
              element={
                <RequireAuth>
                  <StockEntryPage />
                </RequireAuth>
              }
            />
            <Route
              path="/farms/:farmId/inventory/:assetId"
              element={
                <RequireAuth>
                  <AssetPage />
                </RequireAuth>
              }
            />
            <Route
              path="/farms/:farmId/inventory/:assetId/edit"
              element={
                <RequireAuth>
                  <AssetFormPage />
                </RequireAuth>
              }
            />
            <Route
              path="/farms/:farmId/fields/:fieldId"
              element={
                <RequireAuth>
                  <FieldPage />
                </RequireAuth>
              }
            />
            <Route
              path="/farms/:farmId/fields/:fieldId/spray-logs"
              element={
                <RequireAuth>
                  <SprayLogsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/farms/:farmId/fields/:fieldId/fertilizer-logs"
              element={
                <RequireAuth>
                  <FertilizerLogsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/farms/:farmId/fields/:fieldId/amendment-logs"
              element={
                <RequireAuth>
                  <AmendmentLogsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/farms/:farmId/fields/:fieldId/harvest-logs"
              element={
                <RequireAuth>
                  <HarvestLogsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/farms/:farmId/fields/:fieldId/scouting-observations"
              element={
                <RequireAuth>
                  <ScoutingObservationsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/farms/:farmId/fields/:fieldId/field-tasks"
              element={
                <RequireAuth>
                  <FieldTasksPage />
                </RequireAuth>
              }
            />
            <Route
              path="/log"
              element={
                <RequireAuth>
                  <LogStartPage />
                </RequireAuth>
              }
            />
            <Route
              path="/log/:kind"
              element={
                <RequireAuth>
                  <LogFlowPage />
                </RequireAuth>
              }
            />
            <Route
              path="/farms/:farmId/log"
              element={
                <RequireAuth>
                  <LogStartPage />
                </RequireAuth>
              }
            />
            <Route
              path="/farms/:farmId/log/:kind/:draftId?"
              element={
                <RequireAuth>
                  <LogFlowPage />
                </RequireAuth>
              }
            />
            {FARM_RECORD_ROUTES.map(({ path, page }) => (
              <Route key={path} path={`/farms/:farmId/${path}`} element={<RequireAuth>{page}</RequireAuth>} />
            ))}
            <Route
              path="/pesticides"
              element={
                <RequireAuth>
                  <PesticidesPage />
                </RequireAuth>
              }
            />
            <Route
              path="/pesticides/:pesticideId"
              element={
                <RequireAuth>
                  <PesticidePage />
                </RequireAuth>
              }
            />
            <Route
              path="/fertilizers"
              element={
                <RequireAuth>
                  <FertilizersPage />
                </RequireAuth>
              }
            />
            <Route
              path="/fertilizers/:fertilizerId"
              element={
                <RequireAuth>
                  <FertilizerPage />
                </RequireAuth>
              }
            />
            <Route
              path="/ask/:conversationId?"
              element={
                <RequireAuth>
                  <AskPage />
                </RequireAuth>
              }
            />
          </Routes>
        </FarmsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
